import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initialize } from 'redux-form';
import cloneDeep from 'lodash-es/cloneDeep';

import { TagManagerService } from 'app/services/tagManager.service';

import {
  ControlTypeEnum,
  DictionariesEnum,
  FieldsEnum,
  FormsEnum,
  LabelEnum,
  MessageTypeEnum,
  SectionsEnum,
  VehicleTypeEnum,
} from 'app/shared/enums';
import {
  FormsConfigActions,
  getActiveScreen,
  getDictionary,
} from 'app/store/data/formsConfig';
import { getTerminationGeneratorFormConfig } from './terminationGeneratorFormConfig';
import { getOnlineTerminationUrl } from './onlineTerminationInsurers';
import { useForm } from 'app/shared/hooks/useForm';
import { FormConfig } from 'app/shared/interfaces/FormConfig.interface';
import { FormSectionConfigInterface } from 'app/shared/interfaces/FormSectionConfig.interface';
import { TerminationFormInterface } from 'app/shared/interfaces/TerminationForm.interface';
import { TerminationGeneratorService } from 'app/services/terminationGenerator.service';
import { QuestionInterface } from 'app/shared/interfaces/Question.interface';
import { VehicleKindEnum } from 'app/shared/enums/VehicleKind.enum';
import { useDidUpdateEffect } from 'app/shared/hooks/useDidUpdateEffect';
import { useNotification } from 'app/shared/hooks/useNotification';
import { useVehicleType } from 'app/shared/hooks/useVehicleType';
import { QuotationConfigActions } from 'app/store/data/quotationConfig';
import { useUser } from 'app/shared/hooks/useUser';
import { openWindow } from 'app/shared/helpers/openWindow';
import { DictService } from 'app/services/dict.service';
import { RootState } from 'app/store/rootReducer';
import { setDictionaryEntriesVisibility } from 'app/services/businessFunctionHelpers';
import { hasUnconfirmedEmailTypo } from 'app/validators/validateEmailDomainTypo';

const formName = FormsEnum.TerminationGeneratorForm;
const GENERATOR_EXPANDED_KEY = 'generatorExpanded';

const addTerminationGeneratorDataLayer = (event: string) =>
  TagManagerService.addDataLayer({
    event,
    form_name: 'generator_wypowiedzenia_oc',
  });

export function useTerminationGeneratorForm() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSuccessModalOpened, setIsSuccessModalOpened] = useState(false);
  const [
    isOnlineRedirectModalOpened,
    setIsOnlineRedirectModalOpened,
  ] = useState(false);
  const [formValuesCopy, setFormValuesCopy] = useState<
    Partial<TerminationFormInterface>
  >();
  const dispatch = useDispatch();
  const { isMotor } = useVehicleType();
  const { formValues } = useForm<TerminationFormInterface>(formName);
  const notify = useNotification();
  const { consents } = useUser();
  const activeScreen = useSelector((state: RootState) =>
    getActiveScreen(state, formName)
  );

  const insurerNamesDict = useSelector((state: RootState) =>
    getDictionary(
      state,
      formName,
      `${FieldsEnum.TerminationInsurerName}_dictionary`
    )
  );

  const previousInsurerNamesDict = useSelector((state: RootState) =>
    getDictionary(
      state,
      formName,
      `${FieldsEnum.TerminationPreviousInsurerName}_dictionary`
    )
  );

  const initialIsExpanded = useMemo(
    () => !!sessionStorage.getItem(GENERATOR_EXPANDED_KEY),
    []
  );

  const isExpanded = activeScreen > 0 || initialIsExpanded;

  const onFail = () => {
    setIsSubmitting(false);
    notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
  };

  const onSuccess = () => {
    addTerminationGeneratorDataLayer('document_sent');
    addTerminationGeneratorDataLayer('success_popup_view');
    setIsSubmitting(false);
    setIsSubmitted(true);
    setIsSuccessModalOpened(true);
  };

  const onSuccessModalClose = () => {
    addTerminationGeneratorDataLayer('success_popup_close');
    setIsSuccessModalOpened(false);
  };

  const expandForm = () => {
    dispatch(FormsConfigActions.setActiveScreenAction({ screen: 1, formName }));
    sessionStorage.setItem(GENERATOR_EXPANDED_KEY, '1');
    DictService.resolveAllDict();
  };

  const extraValidation = () => {
    if (activeScreen !== 0) {
      return true;
    }

    addTerminationGeneratorDataLayer('form_next_step');

    const onlineTerminationUrl = getOnlineTerminationUrl(
      formValues?.previousInsurerName
    );

    if (!onlineTerminationUrl) {
      sessionStorage.setItem(GENERATOR_EXPANDED_KEY, '1');
      return true;
    }

    addTerminationGeneratorDataLayer('redirect_popup_view');
    setIsOnlineRedirectModalOpened(true);

    return false;
  };

  const onOnlineRedirectModalRedirect = () => {
    addTerminationGeneratorDataLayer('redirect_cta_insurer');
    const onlineTerminationUrl = getOnlineTerminationUrl(
      formValues?.previousInsurerName
    );

    if (onlineTerminationUrl) {
      expandForm();
      openWindow(onlineTerminationUrl, '_self');
    }
  };

  const onOnlineRedirectModalContinue = () => {
    addTerminationGeneratorDataLayer('redirect_cta_pdf');
    setIsOnlineRedirectModalOpened(false);
    expandForm();
  };

  const onOnlineRedirectModalClose = () => {
    addTerminationGeneratorDataLayer('redirect_popup_close');
    setIsOnlineRedirectModalOpened(false);
    expandForm();
  };

  const onSubmit = () => {
    const emailTypoUnconfirmed = hasUnconfirmedEmailTypo({
      email: formValues?.emailAddress,
      formName,
      questionKey: FieldsEnum.TerminationEmailAddress,
    });

    if (emailTypoUnconfirmed) return;

    addTerminationGeneratorDataLayer('document_create');
    const {
      car: { engineCapacity, ...restCarValues },
      allConsentsChecked,
      ...restFormValues
    } = formValues;

    const dataToSend = {
      vehicle: {
        engineVolume: engineCapacity,
        ...restCarValues,
      },
      ...restFormValues,
    };

    setIsSubmitting(true);
    TerminationGeneratorService.sendTerminationData(dataToSend)
      .then(onSuccess)
      .catch(onFail);
  };

  const setConsentVisibility = (question: QuestionInterface) => {
    const consentIds = {
      [FieldsEnum.TerminationConsentMarketing]: 'marketing',
      [FieldsEnum.TerminationConsentRegulations]: 'regulations',
    };

    const userConsent = consents?.find(
      (consent) =>
        consent.consentId ===
        consentIds[question.key as keyof typeof consentIds]
    );

    if (userConsent?.granted) {
      question.visible = false;
    }
  };

  const parseQuestions = (questions: QuestionInterface[]) => {
    questions.forEach((question) => {
      if (
        question.key === FieldsEnum.TerminationConsentMarketing ||
        question.key === FieldsEnum.TerminationConsentRegulations
      ) {
        setConsentVisibility(question);
      }
    });
  };

  const initCheckAllConsentsCheckbox = (
    section: FormSectionConfigInterface
  ) => {
    if (section.key !== SectionsEnum.TerminationGeneratorConsents) {
      return;
    }

    const unselectedConsents = section.questions.filter(
      (question) =>
        question.modelPath.startsWith('consents') && question.visible
    );

    if (unselectedConsents.length > 1) {
      addCheckAllConsentsCheckbox(section);
    }
  };

  const addCheckAllConsentsCheckbox = (section: FormSectionConfigInterface) => {
    const { questions } = section;

    const index = questions.findIndex(
      (row) => row.key === FieldsEnum.ConsentRegulations
    );

    questions.splice(index, 0, {
      key: FieldsEnum.ButtonConsentsTerminationGenerator,
      label: LabelEnum.CheckAll,
      onChange: ['fillAllRequiredTerminationConsents'],
      type: ControlTypeEnum.Checkbox,
      modelPath: '',
      visible: true,
      required: false,
      shouldPatch: false,
      width: 'full',
    });
  };

  const filterQuestionsByVehicleType = (questions: QuestionInterface[]) => {
    const selectedVehicleKind = isMotor
      ? VehicleKindEnum.Motor
      : VehicleKindEnum.Car;

    const filteredQuestions = questions.filter((question) =>
      question.vehicleKind
        ? question.vehicleKind === selectedVehicleKind
        : question
    );

    return filteredQuestions;
  };

  const mapConfig = (formConfig: FormConfig) => {
    const config = cloneDeep(formConfig);

    config.screens.forEach((screen) => {
      screen.sections.forEach((section) => {
        section.questions = filterQuestionsByVehicleType(section.questions);
        parseQuestions(section.questions);
        initCheckAllConsentsCheckbox(section);
      });
    });

    return config;
  };

  const initForm = async () => {
    const defaultFormValues = {
      vehicleType: VehicleTypeEnum.Car,
    };

    const terminationGeneratorFormConfig = await getTerminationGeneratorFormConfig();

    dispatch(
      FormsConfigActions.setFormConfigAction({
        formConfig: mapConfig(terminationGeneratorFormConfig),
        name: formName,
      })
    );

    dispatch(initialize(formName, formValuesCopy ?? defaultFormValues));

    const reachedScreen = sessionStorage.getItem(GENERATOR_EXPANDED_KEY)
      ? 1
      : 0;

    if (reachedScreen > 0) {
      dispatch(
        FormsConfigActions.setActiveScreenAction({
          screen: reachedScreen,
          formName,
        })
      );
    }
  };

  const onVehicleTypeChange = () => {
    if (!formValues?.vehicleType) {
      return;
    }

    const hasVehicleTypeChanged =
      (formValues.vehicleType === VehicleTypeEnum.Motorcycle) !== isMotor;

    const { car, ...values } = formValues;

    if (hasVehicleTypeChanged) {
      setFormValuesCopy(values);
    } else {
      setIsLoaded(true);
    }
  };

  const onFormValuesCopy = () => {
    if (!formValuesCopy) {
      return;
    }

    setIsLoaded(false);
  };

  const triggerFormReinitialize = () => {
    if (isLoaded) {
      return;
    }

    dispatch(
      QuotationConfigActions.setIsMotorConfig(
        formValuesCopy?.vehicleType === VehicleTypeEnum.Motorcycle
      )
    );
  };

  const unlockFormSubmitting = () => {
    if (!isSubmitted) {
      return;
    }

    setIsSubmitted(false);
  };

  const hideNoInsuranceDictEntry = () => {
    if (!insurerNamesDict && !previousInsurerNamesDict) {
      return;
    }

    const dictionaryKeys = [
      `${DictionariesEnum.CarInsurers}_${FieldsEnum.TerminationInsurerName}`,
      `${DictionariesEnum.CarInsurers}_${FieldsEnum.TerminationPreviousInsurerName}`,
    ];

    const entryKeysToHide = ['none', 'gothaer'];

    dictionaryKeys.forEach((dictionaryKey) => {
      setDictionaryEntriesVisibility({
        dictionaryKey,
        entriesKey: entryKeysToHide,
        visible: false,
        formName,
      });
    });
  };

  useEffect(() => {
    addTerminationGeneratorDataLayer('form_start');
  }, []);

  useEffect(() => {
    initForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMotor]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onVehicleTypeChange, [formValues?.vehicleType]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(unlockFormSubmitting, [formValues]);
  useEffect(hideNoInsuranceDictEntry, [
    insurerNamesDict,
    previousInsurerNamesDict,
  ]);
  useDidUpdateEffect(onFormValuesCopy, [formValuesCopy]);
  useDidUpdateEffect(triggerFormReinitialize, [isLoaded]);

  useDidUpdateEffect(() => {
    if (!formValues?.previousInsurerName || !isLoaded) {
      return;
    }

    addTerminationGeneratorDataLayer(
      isExpanded ? 'insurer_change' : 'insurer_select'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.previousInsurerName]);

  return {
    formName,
    isLoaded,
    isSubmitting,
    isSubmitted,
    isSuccessModalOpened,
    isExpanded,
    isOnlineRedirectModalOpened,
    extraValidation,
    onSubmit,
    onSuccessModalClose,
    onOnlineRedirectModalRedirect,
    onOnlineRedirectModalContinue,
    onOnlineRedirectModalClose,
  };
}
